import { MapPin, Phone, Warehouse } from 'lucide-react';
import type { CargoBranch } from '@/data/cargo.api';

type PickupBranchCardProps = {
  branch: CargoBranch;
  language: 'mn' | 'en';
  variant?: 'panel' | 'basic';
};

export function PickupBranchCard({
  branch,
  language,
  variant = 'basic',
}: PickupBranchCardProps) {
  const locationLabel = [branch.city, branch.district].filter(Boolean).join(', ');
  const address = [branch.addressLine1, branch.addressLine2].filter(Boolean).join(', ');

  if (variant === 'panel') {
    return (
      <div className="overflow-hidden rounded-[15px] border border-[var(--ubuy-border-soft)] bg-white">
        {branch.imageUrl && (
          <img
            src={branch.imageUrl}
            alt={branch.name}
            className="h-56 w-full object-cover lg:h-64"
          />
        )}
        <div className="space-y-4 p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] bg-[rgba(246,168,0,0.12)] text-[var(--ubuy-accent)]">
              <Warehouse size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-xl font-bold leading-tight text-[var(--ubuy-title)] md:text-2xl">{branch.name}</p>
              {locationLabel && (
                <p className="mt-1 text-[11px] uppercase tracking-[0.24em] text-[var(--ubuy-soft-muted)]">{locationLabel}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-[12px] border border-[var(--ubuy-border-soft)] bg-[var(--ubuy-subtle-surface)] p-4">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(246,168,0,0.12)] text-[var(--ubuy-accent)]">
                <MapPin size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-[var(--ubuy-title)]">{language === 'mn' ? 'Хаяг' : 'Address'}</p>
                <p className="mt-1 text-sm leading-6 text-[var(--ubuy-muted)]">{address}</p>
              </div>
            </div>

            {branch.phone && (
              <div className="flex items-start gap-3 rounded-[12px] border border-[var(--ubuy-border-soft)] bg-[var(--ubuy-subtle-surface)] p-4">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(246,168,0,0.12)] text-[var(--ubuy-accent)]">
                  <Phone size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[var(--ubuy-title)]">{language === 'mn' ? 'Утас' : 'Phone'}</p>
                  <p className="mt-1 text-sm leading-6 text-[var(--ubuy-muted)]">{branch.phone}</p>
                </div>
              </div>
            )}

            {branch.pickupInstructions && (
              <div className="rounded-[12px] border border-[var(--ubuy-border-soft)] bg-[var(--ubuy-subtle-surface)] p-4">
                <p className="text-sm font-bold text-[var(--ubuy-title)]">
                  {language === 'mn' ? 'Авах заавар' : 'Pickup instructions'}
                </p>
                <p className="mt-1 text-sm leading-6 text-[var(--ubuy-muted)]">{branch.pickupInstructions}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {branch.imageUrl && (
        <img
          src={branch.imageUrl}
          alt={branch.name}
          className="h-52 w-full rounded-[12px] object-cover"
        />
      )}
      <p className="text-base font-bold text-[var(--ubuy-title)]">{branch.name}</p>
      {locationLabel && <p className="text-sm text-[var(--ubuy-soft-muted)]">{locationLabel}</p>}
      <p className="text-sm text-[var(--ubuy-muted)]">{address}</p>
      {branch.phone && (
        <p className="text-sm text-[var(--ubuy-muted)]">
          {language === 'mn' ? 'Утас' : 'Phone'}: {branch.phone}
        </p>
      )}
      {branch.pickupInstructions && (
        <div className="rounded-[10px] border border-[var(--ubuy-border-soft)] bg-[var(--ubuy-subtle-surface)] p-3 text-sm text-[var(--ubuy-muted)]">
          {branch.pickupInstructions}
        </div>
      )}
    </div>
  );
}
